import React, { useState } from 'react';
import { 
  Users, 
  UserPlus,
  TrendingUp,
  RefreshCw,
  Filter,
  Download,
  ChevronRight,
  Activity,
  BarChart3,
  Target,
  Award,
  Briefcase,
  Clock,
  AlertCircle,
  Building2,
  DollarSign,
  TrendingDown,
  UserCheck,
  FileText,
  Calendar,
  Percent,
  PieChart,
  CheckCircle,
  XCircle,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Globe,
  Zap,
  Database,
  Eye,
  Settings,
  Bell,
  Search,
  Star,
  Shield,
  MessageSquare,
  ClipboardList,
  User
} from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart as RechartsBarChart, Bar, PieChart as RechartsPieChart, Pie, Cell, ComposedChart } from 'recharts';

// Sliding KPI Card Component
const SlidingKPICard = ({ slides }) => {
  const [slideIndex, setSlideIndex] = useState(0);
  
  React.useEffect(() => {
    const interval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % slides.length);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [slides.length]);

  const currentSlide = slides[slideIndex];
  const Icon = currentSlide.icon;

  return (
    <div 
      className="relative bg-white rounded-xl p-4 border-2 border-gray-100 transition-all duration-300 overflow-hidden"
      style={{ minHeight: '140px' }}
    >
      <div 
        className="absolute inset-0 transition-all duration-500"
        style={{
          background: `linear-gradient(135deg, ${currentSlide.lightColor} 0%, white 100%)`,
          opacity: 0.3
        }}
      ></div>

      <div className="relative z-10 h-full">
        <div 
          key={slideIndex}
          className="h-full flex flex-col animate-slideIn"
        >
          <div className="flex items-start justify-between mb-3">
            <div 
              className="rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ 
                backgroundColor: currentSlide.lightColor,
                width: '44px',
                height: '44px'
              }}
            >
              <Icon style={{ 
                color: currentSlide.color,
                width: '22px',
                height: '22px'
              }} />
            </div>
          </div>

          <div className="mt-auto">
            <div 
              className="font-semibold mb-1"
              style={{ 
                color: '#111827',
                fontSize: '24px',
                lineHeight: '1.2',
                wordBreak: 'break-word'
              }}
            >
              {currentSlide.value}
            </div>
            <div 
              className="font-normal text-gray-700 uppercase"
              style={{
                fontSize: '9px',
                lineHeight: '1.3',
                letterSpacing: '0.05em'
              }}
            >
              {currentSlide.label}
            </div>
          </div>
        </div>
      </div>

      <div className="absolute top-2 right-2 flex space-x-1 z-20">
        {slides.map((_, i) => (
          <div
            key={i}
            className={`transition-all duration-300 rounded-full ${
              i === slideIndex 
                ? 'w-4 h-1.5 bg-gray-700' 
                : 'w-1.5 h-1.5 bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const AdminDashboard = ({ API_BASE_URL = 'https://test.vdpl.co/Candidates/Candidates', candidates, currentUser, dataLoading, insightsData, buFilter, setBuFilter, viewMode, setViewMode, uniqueBUHeads, setSelectedReasonModal, setShowBUDrilldown, setShowClientDrilldown, showBUDrilldown, showClientDrilldown, clientFilter, setClientFilter }) => {
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedBUs, setSelectedBUs] = useState([]);
  const [timeRange, setTimeRange] = useState('6m');
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showBUDropdown, setShowBUDropdown] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMonths, setIsLoadingMonths] = useState(true);
  const [vipSlideIndex, setVipSlideIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [vipCandidatesData, setVipCandidatesData] = useState([
    { category: '25+ Years', count: 0, color: '#7c3aed', bgColor: 'from-white to-white', iconName: 'Award' },
    { category: '10+ Years', count: 0, color: '#0047bb', bgColor: 'from-white to-white', iconName: 'Star' },
    { category: '5+ Years', count: 0, color: '#10b981', bgColor: 'from-white to-white', iconName: 'Shield' },
    { category: '2-4 Years', count: 0, color: '#3b82f6', bgColor: 'from-white to-white', iconName: 'TrendingUp' },
    { category: '0-1 Year', count: 0, color: '#f59e0b', bgColor: 'from-white to-white', iconName: 'Zap' }
  ]);

  const [rollOffAlertsData, setRollOffAlertsData] = useState({
    total: 0,
    sixtyDays: 0,
    thirtyDays: 0,
    sevenDays: 0
  });

  const [isLoadingData, setIsLoadingData] = useState(true);
  const [buHeadsData, setBuHeadsData] = useState([]);
  const [isLoadingBUData, setIsLoadingBUData] = useState(true);
  const [clientsData, setClientsData] = useState([]);
  const [isLoadingClientsData, setIsLoadingClientsData] = useState(true);
  const [notExtendedReasons, setNotExtendedReasons] = useState([]);
  const [isLoadingReasonsData, setIsLoadingReasonsData] = useState(true);
  const [extensionFrequencyData, setExtensionFrequencyData] = useState([]);
  const [isLoadingFrequencyData, setIsLoadingFrequencyData] = useState(true);
  const [extensionDurationData, setExtensionDurationData] = useState([]);
  const [isLoadingDurationData, setIsLoadingDurationData] = useState(true);

  const [kpiMetrics, setKpiMetrics] = useState({
    activeCandidates: 0,
    inactiveCandidates: 0,
    uniqueClients: 0,
    endingSoon: 0,
    projectsEnded: 0,
    totalExtended: 0,
    notExtended: 0,
    currentRevenue: 0,
    revenueAtRisk: 0,
    revenueSecured: 0
  });

  const monthlyTrendData = [
    { month: 'Jan', Extended: 45, NotExtended: 12, Revenue: 2.8, Target: 3.0 },
    { month: 'Feb', Extended: 52, NotExtended: 15, Revenue: 3.1, Target: 3.2 },
    { month: 'Mar', Extended: 48, NotExtended: 18, Revenue: 2.9, Target: 3.3 },
    { month: 'Apr', Extended: 61, NotExtended: 14, Revenue: 3.5, Target: 3.4 },
    { month: 'May', Extended: 58, NotExtended: 16, Revenue: 3.3, Target: 3.5 },
    { month: 'Jun', Extended: 67, NotExtended: 11, Revenue: 3.8, Target: 3.6 }
  ];

  const getFilterParams = () => {
    const years = selectedYears.length > 0 ? selectedYears.join(',') : 'all';
    const months = selectedMonths.length > 0 ? selectedMonths.join(',') : 'all';
    const bus = selectedBUs.length > 0 ? selectedBUs.join(',') : 'all';
    return { years, months, bus };
  };

  const getRoleParams = () => {
    if (currentUser?.role === 'BU Head' && currentUser?.email) {
      return `&bu_head_email=${encodeURIComponent(currentUser.email)}`;
    }
    return '';
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    const filters = getFilterParams();
    fetchAvailableMonths();
    fetchDashboardData(filters);
    fetchBUData(filters);
    fetchClientsData(filters);
    fetchNotExtendedReasons(filters);
    fetchExtensionFrequency(filters);
    fetchExtensionDuration(filters);
    setTimeout(() => setIsRefreshing(false), 1500);
  };

  React.useEffect(() => {
    fetchAvailableMonths();
    const filters = { years: 'all', months: 'all', bus: 'all' };
    fetchDashboardData(filters);
    fetchBUData(filters);
    fetchClientsData(filters);
    fetchNotExtendedReasons(filters);
    fetchExtensionFrequency(filters);
    fetchExtensionDuration(filters);
  }, []);

  React.useEffect(() => {
    const filters = getFilterParams();
    fetchDashboardData(filters);
    fetchBUData(filters);
    fetchClientsData(filters);
    fetchNotExtendedReasons(filters);
    fetchExtensionFrequency(filters);
    fetchExtensionDuration(filters);
  }, [selectedYears, selectedMonths, selectedBUs]);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowYearDropdown(false);
        setShowMonthDropdown(false);
        setShowBUDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchDashboardData = async (filters) => {
    setIsLoadingData(true);
    try {
      const response = await fetch(
  `${API_BASE_URL}/get_candidates.php?years=${filters.years}&months=${filters.months}&bus=${filters.bus}${getRoleParams()}`
);
const result = await response.json();
console.log('AdminDashboard fetchDashboardData result:', result.success, 'count:', result.data?.length);
console.log('Sample:', result.data?.[0]);

      if (result.success && result.data) {
  const candidates = result.data;
        const activeCandidatesCount = candidates.filter(c => c.status?.toLowerCase() === 'active').length;
const inactiveCandidatesCount = candidates.filter(c => c.status?.toLowerCase() !== 'active').length;
const uniqueClientsSet = new Set(candidates.filter(c => c.status?.toLowerCase() === 'active').map(c => c.client));
        const uniqueClientsCount = uniqueClientsSet.size;
        const today = new Date();

        const rollOffCounts = { total: 0, sixtyDays: 0, thirtyDays: 0, sevenDays: 0 };
        let endingSoonCount = 0;
        let projectsEndedCount = 0;
        let totalExtendedCount = 0;
        let notExtendedCount = 0;
        let currentRevenueSum = 0;
        let revenueAtRiskSum = 0;
        let revenueSecuredSum = 0;

        candidates.forEach(candidate => {
          if (candidate.status === 'Active' && candidate.clientRate && candidate.startDate && candidate.sowEndDate) {
            const startDate = new Date(candidate.startDate);
            const endDate = new Date(candidate.sowEndDate);
            const totalContractDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
            if (totalContractDays > 0) {
              const totalHours = totalContractDays * 8;
              currentRevenueSum += totalHours * (parseFloat(candidate.clientRate) || 0);
            }
          }

          if (candidate.extensionStatus?.toLowerCase() === 'yes') {
            totalExtendedCount++;
            if (candidate.clientRate) {
              revenueSecuredSum += parseFloat(candidate.clientRate) || 0;
            }
          }

          if (candidate.extensionStatus?.toLowerCase() === 'no') {
            projectsEndedCount++;
            notExtendedCount++;
          }

          if (candidate.status?.toLowerCase() === 'active' && candidate.sowEndDate) {
            const endDate = new Date(candidate.sowEndDate);
            const daysUntilEnd = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));

            if (daysUntilEnd > 0 && daysUntilEnd <= 60) {
              rollOffCounts.total++;
              if (candidate.clientRate) {
                const hoursAtRisk = daysUntilEnd * 8;
                revenueAtRiskSum += hoursAtRisk * (parseFloat(candidate.clientRate) || 0);
              }
              if (daysUntilEnd <= 7) {
                rollOffCounts.sevenDays++;
              } else if (daysUntilEnd > 7 && daysUntilEnd <= 30) {
                rollOffCounts.thirtyDays++;
              } else if (daysUntilEnd > 30 && daysUntilEnd <= 60) {
                rollOffCounts.sixtyDays++;
              }
            }
          }
        });

        endingSoonCount = rollOffCounts.total;

        setKpiMetrics({
          activeCandidates: activeCandidatesCount,
          inactiveCandidates: inactiveCandidatesCount,
          uniqueClients: uniqueClientsCount,
          endingSoon: endingSoonCount,
          projectsEnded: projectsEndedCount,
          totalExtended: totalExtendedCount,
          notExtended: notExtendedCount,
          currentRevenue: currentRevenueSum,
          revenueAtRisk: revenueAtRiskSum,
          revenueSecured: revenueSecuredSum
        });

        setRollOffAlertsData(rollOffCounts);

        const vipCounts = { '25+': 0, '10+': 0, '5+': 0, '2-4': 0, '0-1': 0 };

        candidates.forEach(candidate => {
          if (candidate.status?.toLowerCase() === 'active' && candidate.startDate) {
            const startDate = new Date(candidate.startDate);
            const yearsWithVDart = (today - startDate) / (1000 * 60 * 60 * 24 * 365);
            if (yearsWithVDart >= 25) vipCounts['25+']++;
            else if (yearsWithVDart >= 10) vipCounts['10+']++;
            else if (yearsWithVDart >= 5) vipCounts['5+']++;
            else if (yearsWithVDart >= 2) vipCounts['2-4']++;
            else if (yearsWithVDart >= 0) vipCounts['0-1']++;
          }
        });

        setVipCandidatesData([
          { category: '25+ Years', count: vipCounts['25+'], color: '#7c3aed', bgColor: 'from-white to-white', iconName: 'Award' },
          { category: '10+ Years', count: vipCounts['10+'], color: '#0047bb', bgColor: 'from-white to-white', iconName: 'Star' },
          { category: '5+ Years', count: vipCounts['5+'], color: '#10b981', bgColor: 'from-white to-white', iconName: 'Shield' },
          { category: '2-4 Years', count: vipCounts['2-4'], color: '#3b82f6', bgColor: 'from-white to-white', iconName: 'TrendingUp' },
          { category: '0-1 Year', count: vipCounts['0-1'], color: '#f59e0b', bgColor: 'from-white to-white', iconName: 'Zap' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  const fetchBUData = async (filters) => {
  setIsLoadingBUData(true);
  try {
    const response = await fetch(
  `${API_BASE_URL}/get_bu_stats.php?years=${filters.years}&months=${filters.months}&bus=${filters.bus}${getRoleParams()}`
);
    const result = await response.json();

    console.log('🔍 BU Stats Raw Response:', JSON.stringify(result, null, 2));

    if (result.success && result.data && result.data.length > 0) {
      const colors = [
        'from-[#0047bb] to-[#0066ff]',
        'from-[#0047bb] to-[#0088ff]',
        'from-[#0047bb] to-[#0077ff]',
        'from-[#0047bb] to-[#0055cc]',
        'from-[#0047bb] to-[#0099ff]'
      ];

      const mappedData = result.data.map((bu, index) => {
        console.log(`📦 Mapping BU [${index}]:`, bu);

        return {
          name:        bu.name        || 'Unknown',
          title:       `${bu.name || 'Unknown'}'s Team`,
          avatar:      (bu.name || 'U').charAt(0).toUpperCase(),
          color:       colors[index % colors.length],
          bgColor:     'from-white to-white',
          totalActive: bu.totalActive || 0,

          rollOff: {
            total:        bu.rollOff?.total        ?? 0,
            connected:    bu.rollOff?.connected    ?? 0,
            notConnected: bu.rollOff?.notConnected ?? 0,
          },

          contracts: {
            extended:       bu.contracts?.extended       ?? 0,
            pending:        bu.contracts?.pending        ?? 0,
            contractEnded:  bu.contracts?.contractEnded  ?? 0,
          },

          coiPercentage:  bu.coiPercentage  ?? 0,
          workEmail:      bu.workEmail      ?? 0,
          reportingInfo:  bu.reportingInfo  ?? 0,
          projectDetails: bu.projectDetails ?? 0,
          clientFeedback: bu.clientFeedback ?? 0,
        };
      });

      console.log('✅ Mapped BU Data:', mappedData);
      setBuHeadsData(mappedData);

    } else {
      console.warn('⚠️ BU API returned no data:', result);
      setBuHeadsData([]);
    }
  } catch (error) {
    console.error('❌ Error fetching BU data:', error);
    setBuHeadsData([]);
  } finally {
    setIsLoadingBUData(false);
  }
};

  const fetchClientsData = async (filters) => {
    setIsLoadingClientsData(true);
    try {
      const response = await fetch(
  `${API_BASE_URL}/get_client_stats.php?years=${filters.years}&months=${filters.months}&bus=${filters.bus}${getRoleParams()}`
);
      const result = await response.json();
      if (result.success && result.data) {
        setClientsData(result.data);
      } else {
        setClientsData([]);
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
      setClientsData([]);
    } finally {
      setIsLoadingClientsData(false);
    }
  };

  const fetchNotExtendedReasons = async (filters) => {
    setIsLoadingReasonsData(true);
    try {
      const response = await fetch(
  `${API_BASE_URL}/get_not_extended_reasons.php?years=${filters.years}&months=${filters.months}&bus=${filters.bus}${getRoleParams()}`
);
      const result = await response.json();

      const allReasons = [
        'Resignation',
        'Project Completion',
        'FTE Conversion by Client',
        'Termination',
        'Work Authorization Expiry'
      ];

      if (result.success && result.data) {
        const displayReasons = allReasons.map(reasonName => {
          const found = result.data.find(r => r.reason === reasonName);
          return found || { reason: reasonName, count: 0, percentage: 0 };
        });
        setNotExtendedReasons(displayReasons);
      } else {
        setNotExtendedReasons(allReasons.map(r => ({ reason: r, count: 0, percentage: 0 })));
      }
    } catch (error) {
      console.error('Error fetching reasons data:', error);
      setNotExtendedReasons([
        { reason: 'Resignation', count: 0, percentage: 0 },
        { reason: 'Project Completion', count: 0, percentage: 0 },
        { reason: 'FTE Conversion by Client', count: 0, percentage: 0 },
        { reason: 'Termination', count: 0, percentage: 0 },
        { reason: 'Work Authorization Expiry', count: 0, percentage: 0 }
      ]);
    } finally {
      setIsLoadingReasonsData(false);
    }
  };

  const fetchExtensionFrequency = async (filters) => {
    setIsLoadingFrequencyData(true);
    try {
      const response = await fetch(
  `${API_BASE_URL}/get_extension_frequency.php?years=${filters.years}&months=${filters.months}&bus=${filters.bus}${getRoleParams()}`
);
      const result = await response.json();
      if (result.success && result.data) {
        setExtensionFrequencyData(result.data);
      } else {
        setExtensionFrequencyData([]);
      }
    } catch (error) {
      console.error('Error fetching frequency data:', error);
      setExtensionFrequencyData([]);
    } finally {
      setIsLoadingFrequencyData(false);
    }
  };

  const fetchExtensionDuration = async (filters) => {
    setIsLoadingDurationData(true);
    try {
      const response = await fetch(
 `${API_BASE_URL}/get_extension_duration.php?years=${filters.years}&months=${filters.months}&bus=${filters.bus}${getRoleParams()}`
);
      const result = await response.json();
      if (result.success && result.data) {
        setExtensionDurationData(result.data);
      } else {
        setExtensionDurationData([]);
      }
    } catch (error) {
      console.error('Error fetching duration data:', error);
      setExtensionDurationData([]);
    } finally {
      setIsLoadingDurationData(false);
    }
  };

  const fetchAvailableMonths = async () => {
    setIsLoadingMonths(true);
    try {
      const response = await fetch(`${API_BASE_URL}/get_available_months.php`);
      const result = await response.json();
      if (result.success && result.data) {
        setAvailableMonths(result.data);
      } else {
        setAvailableMonths([]);
      }
    } catch (error) {
      console.error('Error fetching available months:', error);
      setAvailableMonths([]);
    } finally {
      setIsLoadingMonths(false);
    }
  };

  React.useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        setVipSlideIndex((prev) => (prev + 1) % vipCandidatesData.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isPaused]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white text-gray-900 p-3 sm:p-4 rounded-xl shadow-2xl border-2 border-[#0047bb]/20">
          <p className="text-xs sm:text-sm font-bold mb-2 text-gray-800">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between space-x-4 py-1">
              <div className="flex items-center space-x-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></span>
                <span className="text-xs text-gray-600">{entry.name}</span>
              </div>
              <span className="text-sm font-bold text-gray-900">{entry.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-xl border-b border-[#0047bb]/10 sticky top-0 z-30 shadow-lg shadow-[#0047bb]/5">
        <div className="w-full py-4 lg:py-5">
          <div className="flex items-center justify-between flex-wrap gap-3 lg:gap-4 px-5">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div>
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#0047bb]">Executive Dashboard</h1>
                <p className="text-xs sm:text-sm text-gray-600 font-medium hidden sm:block">Real-time Analytics & Insights</p>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3 flex-wrap gap-2">
              <div className="relative">
                {/* Year filter (commented out in original) */}
              </div>

              <div className="relative">
                {/* Month filter (commented out in original) */}
              </div>

              {/* Business Unit Multi-Select Filter */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowBUDropdown(!showBUDropdown);
                    setShowYearDropdown(false);
                    setShowMonthDropdown(false);
                  }}
                  className="flex items-center space-x-2 bg-white rounded-lg sm:rounded-xl px-3 lg:px-4 py-1.5 sm:py-2 border border-gray-200 shadow-sm hover:shadow-md transition-all"
                >
                  <Filter className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-[#0047bb]" />
                  <span className="text-xs sm:text-sm font-semibold text-gray-900">
                    Business Units {selectedBUs.length > 0 && `(${selectedBUs.length})`}
                  </span>
                  <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showBUDropdown ? 'rotate-90' : ''}`} />
                </button>

                {showBUDropdown && (
                  <div className="absolute top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 min-w-[250px] max-h-[300px] overflow-y-auto">
                    <div className="p-2">
                      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 mb-2">
                        <span className="text-xs font-bold text-gray-700">Select Business Units</span>
                        <button
                          onClick={() => setSelectedBUs([])}
                          className="text-xs text-[#0047bb] hover:underline font-semibold"
                        >
                          Clear All
                        </button>
                      </div>
                      {!isLoadingBUData && buHeadsData.map((bu, idx) => (
                        <label key={idx} className="flex items-center space-x-3 px-3 py-2 hover:bg-blue-50 rounded-lg cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedBUs.includes(bu.name)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedBUs([...selectedBUs, bu.name]);
                              } else {
                                setSelectedBUs(selectedBUs.filter(b => b !== bu.name));
                              }
                            }}
                            className="w-4 h-4 text-[#0047bb] border-gray-300 rounded focus:ring-[#0047bb]"
                          />
                          <span className="text-sm text-gray-900 font-medium">{bu.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={handleRefresh}
                className="px-3 sm:px-4 lg:px-5 py-2 lg:py-2.5 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-[#0047bb] to-[#0066ff] rounded-lg sm:rounded-xl hover:from-[#003399] hover:to-[#0055cc] transition-all flex items-center space-x-1.5 sm:space-x-2 shadow-lg shadow-[#0047bb]/30"
              >
                <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-1 py-4 sm:py-6 lg:py-8 space-y-4 sm:space-y-6 lg:space-y-8">

        {/* Hero KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-3 lg:gap-4">
          {[
            { icon: UserCheck,   label: 'Active / Inactive', value: isLoadingData ? '...' : `${kpiMetrics.activeCandidates} / ${kpiMetrics.inactiveCandidates}`, positive: true, color: '#0047bb', lightColor: '#e6f0ff', type: 'normal' },
            { icon: Building2,   label: 'Total Clients',   value: isLoadingData ? '...' : kpiMetrics.uniqueClients,     positive: true,  color: '#0066ff', lightColor: '#e6f0ff', type: 'normal' },
            { icon: AlertCircle, label: 'Ending Soon',     value: isLoadingData ? '...' : kpiMetrics.endingSoon,        positive: null,  color: '#f97316', lightColor: '#ffedd5', type: 'normal' },
            { icon: CheckCircle, label: 'Total Extended',  value: isLoadingData ? '...' : kpiMetrics.totalExtended,     positive: true,  color: '#10b981', lightColor: '#d1fae5', type: 'normal' },
            { icon: Clock,       label: 'Projects Ended',  value: isLoadingData ? '...' : kpiMetrics.projectsEnded,     positive: true,  color: '#a855f7', lightColor: '#f3e8ff', type: 'normal' },
            { icon: DollarSign,  label: 'Current',         value: isLoadingData ? '...' : `$${(kpiMetrics.currentRevenue / 1000000).toFixed(1)}M`, positive: true, color: '#10b981', lightColor: '#d1fae5', type: 'normal' },
            {
              icon: TrendingDown, label: 'Revenue at Risk',
              value: isLoadingData ? '...' :
                kpiMetrics.revenueAtRisk >= 1000000 ? `$${(kpiMetrics.revenueAtRisk / 1000000).toFixed(1)}M` :
                kpiMetrics.revenueAtRisk >= 1000 ? `$${(kpiMetrics.revenueAtRisk / 1000).toFixed(1)}K` :
                `$${kpiMetrics.revenueAtRisk.toFixed(0)}`,
              positive: false, color: '#ef4444', lightColor: '#fee2e2', type: 'normal'
            },
            
          ].map((card, idx) => {
            if (card.type === 'slider') {
              return <SlidingKPICard key={idx} slides={card.slides} />;
            }
            const Icon = card.icon;
            return (
              <div
                key={idx}
                className="group relative bg-white rounded-xl p-4 border-2 border-gray-100 transition-all duration-300 overflow-hidden"
                style={{ minHeight: '140px' }}
              >
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: `linear-gradient(135deg, ${card.lightColor} 0%, white 100%)` }}
                ></div>
                <div className="relative z-10 h-full flex flex-col">
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className="rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: card.lightColor, width: '44px', height: '44px' }}
                    >
                      <Icon style={{ color: card.color, width: '22px', height: '22px' }} />
                    </div>
                  </div>
                  <div className="mt-auto">
                    <div className="font-semibold mb-1" style={{ color: '#111827', fontSize: '24px', lineHeight: '1.2', wordBreak: 'break-word' }}>
                      {card.value}
                    </div>
                    <div className="font-normal text-gray-700 uppercase" style={{ fontSize: '9px', lineHeight: '1.3', letterSpacing: '0.05em' }}>
                      {card.label}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <style jsx>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateX(100%); }
            to   { opacity: 1; transform: translateX(0); }
          }
          .animate-slideIn { animation: slideIn 0.5s ease-out; }
        `}</style>

        {/* VIP Candidates & Roll-off Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">

          {/* VIP Candidates */}
          <div className="bg-white rounded-xl lg:rounded-2xl p-2.5 lg:p-3 border border-gray-200/50 shadow-xl shadow-[#0047bb]/5 overflow-hidden">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm lg:text-base font-bold text-[#0047bb] mb-0.5">VIP Candidates</h3>
                <p className="text-[9px] text-gray-600">Categorized by VDart experience</p>
              </div>
              <div className="w-8 h-8 bg-gradient-to-br from-[#0047bb]/10 to-[#0066ff]/10 rounded-lg flex items-center justify-center">
                <Award className="w-4 h-4 text-[#0047bb]" />
              </div>
            </div>

            <div className="mb-2 p-1.5 bg-gradient-to-br from-[#0047bb]/5 to-[#0066ff]/5 rounded-lg border border-[#0047bb]/10">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[8px] text-gray-600 font-medium mb-0.5">Total VIP Candidates</div>
                  <div className="text-lg font-bold text-[#0047bb]">
                    {isLoadingData ? <div className="animate-pulse">...</div> : vipCandidatesData.reduce((sum, cat) => sum + cat.count, 0)}
                  </div>
                </div>
                <div className="w-9 h-9 bg-gradient-to-br from-[#0047bb] to-[#0066ff] rounded-lg flex items-center justify-center shadow-md shadow-[#0047bb]/30">
                  <Users className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-5 gap-1.5">
              {[
                { label: '25+ Years', sub: 'Veterans',    idx: 0, bg: 'from-purple-50 to-white', border: 'border-purple-200/50', numColor: 'text-purple-600', iconBg: 'from-purple-500 to-purple-600', Icon: Award },
                { label: '10+ Years', sub: 'Senior',      idx: 1, bg: 'from-blue-50 to-white',   border: 'border-blue-200/50',   numColor: 'text-[#0047bb]',  iconBg: 'from-[#0047bb] to-[#0066ff]', Icon: Star },
                { label: '5+ Years',  sub: 'Experienced', idx: 2, bg: 'from-green-50 to-white',  border: 'border-green-200/50',  numColor: 'text-green-600',  iconBg: 'from-green-500 to-green-600', Icon: Shield },
                { label: '2-4 Years', sub: 'Mid-Level',   idx: 3, bg: 'from-sky-50 to-white',    border: 'border-sky-200/50',    numColor: 'text-sky-600',    iconBg: 'from-sky-500 to-sky-600',     Icon: TrendingUp },
                { label: '0-1 Year',  sub: 'New Recruits',idx: 4, bg: 'from-amber-50 to-white',  border: 'border-amber-200/50',  numColor: 'text-amber-600',  iconBg: 'from-amber-500 to-amber-600', Icon: Zap },
              ].map(({ label, sub, idx, bg, border, numColor, iconBg, Icon }) => (
                <div key={idx} className={`group relative bg-gradient-to-br ${bg} rounded-lg p-1.5 border ${border} hover:shadow-md transition-all duration-300 overflow-hidden`}>
                  <div className="relative z-10">
                    <div className="flex items-center justify-center mb-1">
                      <div className={`w-6 h-6 bg-gradient-to-br ${iconBg} rounded-md flex items-center justify-center shadow-md`}>
                        <Icon className="w-3.5 h-3.5 text-white" />
                      </div>
                    </div>
                    <div className="text-center">
                      <div className={`text-lg font-bold ${numColor} mb-0.5`}>
                        {isLoadingData ? '...' : vipCandidatesData[idx]?.count ?? 0}
                      </div>
                      <div className="text-[8px] font-bold text-gray-900 mb-0.5">{label}</div>
                      <div className="text-[7px] text-gray-600">{sub}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Roll-off Alerts */}
          <div className="bg-white rounded-xl lg:rounded-2xl p-2.5 lg:p-3 border border-gray-200/50 shadow-xl shadow-[#0047bb]/5 overflow-hidden">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm lg:text-base font-bold text-[#0047bb] mb-0.5">Roll-off Alerts</h3>
                <p className="text-[9px] text-gray-600">Candidates ending soon by timeline</p>
              </div>
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-4 h-4 text-indigo-600" />
              </div>
            </div>

            <div className="mb-2 p-1.5 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border border-indigo-200/30">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[8px] text-gray-600 font-medium mb-0.5">Total Alerts</div>
                  <div className="text-lg font-bold text-indigo-600">
                    {isLoadingData ? <div className="animate-pulse">...</div> : rollOffAlertsData.total}
                  </div>
                </div>
                <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center shadow-md shadow-indigo-500/30">
                  <Bell className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-1.5">
              <div className="group relative bg-gradient-to-br from-blue-50 to-white rounded-lg p-1.5 border border-blue-200/50 hover:shadow-md transition-all duration-300 overflow-hidden">
                <div className="relative z-10">
                  <div className="flex items-center justify-center mb-1">
                    <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md flex items-center justify-center shadow-md">
                      <Clock className="w-3.5 h-3.5 text-white" />
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-600 mb-0.5">{isLoadingData ? '...' : rollOffAlertsData.sixtyDays}</div>
                    <div className="text-[8px] font-bold text-gray-900 mb-0.5">60 Days</div>
                    <div className="text-[7px] text-gray-600">Low Priority</div>
                  </div>
                </div>
              </div>

              <div className="group relative bg-gradient-to-br from-indigo-50 to-white rounded-lg p-1.5 border border-indigo-200/50 hover:shadow-md transition-all duration-300 overflow-hidden">
                <div className="relative z-10">
                  <div className="flex items-center justify-center mb-1">
                    <div className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-md flex items-center justify-center shadow-md">
                      <AlertCircle className="w-3.5 h-3.5 text-white" />
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-indigo-600 mb-0.5">{isLoadingData ? '...' : rollOffAlertsData.thirtyDays}</div>
                    <div className="text-[8px] font-bold text-gray-900 mb-0.5">30 Days</div>
                    <div className="text-[7px] text-gray-600">Medium Priority</div>
                  </div>
                </div>
              </div>

              <div className="group relative bg-gradient-to-br from-purple-50 to-white rounded-lg p-1.5 border-2 border-purple-300/70 hover:shadow-md transition-all duration-300 overflow-hidden animate-pulse-slow">
                <div className="relative z-10">
                  <div className="flex items-center justify-center mb-1">
                    <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-purple-600 rounded-md flex items-center justify-center shadow-md relative">
                      <Bell className="w-3.5 h-3.5 text-white" />
                      <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-purple-600 rounded-full animate-ping"></span>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-purple-600 mb-0.5">{isLoadingData ? '...' : rollOffAlertsData.sevenDays}</div>
                    <div className="text-[8px] font-bold text-gray-900 mb-0.5">7 Days</div>
                    <div className="text-[7px] text-gray-600">High Priority</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
          @keyframes pulse-slow { 0%, 100% { opacity: 1; } 50% { opacity: 0.95; } }
          .animate-pulse-slow { animation: pulse-slow 3s ease-in-out infinite; }
        `}</style>

        {/* BU Heads Performance Cards */}
        <div>
          <div className="flex items-center justify-between mb-4 sm:mb-5">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-[#0047bb]">Business Unit Leaders</h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Performance overview by BU Head</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-5">
            {isLoadingBUData ? (
              <div className="col-span-full flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-[#0047bb] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading BU data...</p>
                </div>
              </div>
            ) : buHeadsData.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-600">No BU heads found in database</p>
              </div>
            ) : (
              buHeadsData.map((bu, idx) => {
                const coiColor = bu.coiPercentage >= 80 ? '#10b981' : bu.coiPercentage >= 50 ? '#f59e0b' : '#ef4444';
                const connPct = bu.rollOff.total > 0 ? Math.round((bu.rollOff.connected / bu.rollOff.total) * 100) : 0;

                return (
                  <div
                    key={idx}
                    className={`relative bg-gradient-to-br ${bu.bgColor} rounded-xl lg:rounded-2xl border border-gray-200/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden`}
                  >
                    {/* Dot pattern background */}
                    <div className="absolute inset-0 opacity-5">
                      <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)', backgroundSize: '24px 24px' }}></div>
                    </div>

                    <div className="relative z-10 p-4 sm:p-5 flex flex-col gap-3">

                      {/* Header: Avatar + Name + Active */}
                      <div className="flex items-start justify-between">
                        <div className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${bu.color} rounded-xl flex items-center justify-center shadow-lg shadow-[#0047bb]/30 font-semibold text-white text-lg sm:text-xl`}>
                          {bu.avatar}
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg px-3 py-2 border border-blue-200/50 shadow-sm text-center">
                          <div className="text-xl font-bold text-[#0047bb] leading-none mb-0.5">{bu.totalActive}</div>
                          <div className="text-[8px] font-semibold text-gray-600 uppercase tracking-wide">Active</div>
                        </div>
                      </div>

                      {/* Name & Title */}
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-tight">{bu.name}</h3>
                        <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mt-0.5">{bu.title}</p>
                      </div>

                      {/* 1. Roll-off + Connected/Not Connected */}
                      <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-indigo-500" />
                            <span className="text-[11px] font-semibold text-gray-700">Total Roll-off</span>
                          </div>
                          <span className="text-[13px] font-bold text-gray-900">{bu.rollOff.total}</span>
                        </div>
                        <div className="w-full h-2 bg-red-100 rounded-full overflow-hidden mb-2">
                          <div
                            className="h-full bg-green-500 rounded-full transition-all duration-700"
                            style={{ width: `${connPct}%` }}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center gap-1.5 bg-green-50 rounded-lg px-2 py-1.5 border border-green-100">
                            <UserCheck className="w-3 h-3 text-green-600 flex-shrink-0" />
                            <div>
                              <div className="text-[11px] font-bold text-green-700 leading-none">{bu.rollOff.connected}</div>
                              <div className="text-[8px] text-gray-500">Connected</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 bg-red-50 rounded-lg px-2 py-1.5 border border-red-100">
                            <AlertCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
                            <div>
                              <div className="text-[11px] font-bold text-red-600 leading-none">{bu.rollOff.notConnected}</div>
                              <div className="text-[8px] text-gray-500">Not Connected</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 2. Contracts */}
                      <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                        <div className="flex items-center gap-1.5 mb-2.5">
                          <FileText className="w-3.5 h-3.5 text-blue-500" />
                          <span className="text-[11px] font-semibold text-gray-700">Contracts</span>
                        </div>
                        <div className="grid grid-cols-3 gap-1.5">
                          <div className="flex flex-col items-center bg-green-50 rounded-lg py-2 border border-green-100">
                            <CheckCircle className="w-3.5 h-3.5 text-green-600 mb-0.5" />
                            <span className="text-base font-bold text-green-700 leading-none">{bu.contracts.extended}</span>
                            <span className="text-[8px] text-gray-500 mt-0.5">Extended</span>
                          </div>
                          <div className="flex flex-col items-center bg-orange-50 rounded-lg py-2 border border-orange-100">
                            <Clock className="w-3.5 h-3.5 text-orange-500 mb-0.5" />
                            <span className="text-base font-bold text-orange-600 leading-none">{bu.contracts.pending}</span>
                            <span className="text-[8px] text-gray-500 mt-0.5">Pending</span>
                          </div>
                          <div className="flex flex-col items-center bg-gray-100 rounded-lg py-2 border border-gray-200">
                            <XCircle className="w-3.5 h-3.5 text-gray-500 mb-0.5" />
                            <span className="text-base font-bold text-gray-600 leading-none">{bu.contracts.contractEnded}</span>
                            <span className="text-[8px] text-gray-500 mt-0.5 text-center leading-tight">Ended</span>
                          </div>
                        </div>
                      </div>

                      {/* 3. COI */}
                      <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-1.5">
                            <Shield className="w-3.5 h-3.5" style={{ color: coiColor }} />
                            <span className="text-[11px] font-semibold text-gray-700">COI Completion</span>
                          </div>
                          <span className="text-[11px] font-bold" style={{ color: coiColor }}>{bu.coiPercentage}%</span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${bu.coiPercentage}%`, backgroundColor: coiColor }}
                          />
                        </div>
                      </div>

                      {/* 4. Data Collection */}
                      <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 flex flex-col gap-2.5">
                        {[
                          { Icon: FileText,      label: 'Work Email',      collected: bu.workEmail,      color: '#0047bb' },
                          { Icon: ClipboardList, label: 'Reporting Info',  collected: bu.reportingInfo,  color: '#0066ff' },
                          { Icon: Briefcase,     label: 'Project Details', collected: bu.projectDetails, color: '#10b981' },
                          { Icon: MessageSquare, label: 'Client Feedback', collected: bu.clientFeedback, color: '#f59e0b' },
                        ].map(({ Icon, label, collected, color }) => (
                          <div key={label} className="flex flex-col gap-0.5">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <Icon className="w-3 h-3 text-gray-400" />
                                <span className="text-[10px] text-gray-600">{label}</span>
                              </div>
                              <span className="text-[10px] font-semibold text-gray-800">
                                {collected}<span className="font-normal text-gray-400">/{bu.totalActive}</span>
                              </span>
                            </div>
                            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-700"
                                style={{ width: `${bu.totalActive > 0 ? Math.round((collected / bu.totalActive) * 100) : 0}%`, backgroundColor: color }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>

                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Main Analytics Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 sm:gap-5 lg:gap-6">

          {/* LEFT + CENTER */}
          <div className="xl:col-span-8 space-y-4 sm:space-y-5 lg:space-y-6">

            {/* Client Extension Analysis */}
            <div className="bg-white/90 backdrop-blur-sm rounded-xl lg:rounded-2xl p-4 sm:p-5 lg:p-6 border border-gray-200/50 shadow-xl shadow-[#0047bb]/5">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-[#0047bb] mb-1">Client Extension Analysis</h3>
                  <p className="text-xs sm:text-sm text-gray-600">All clients performance breakdown</p>
                </div>
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-[#0047bb]/10 to-[#0066ff]/10 rounded-lg sm:rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-[#0047bb]" />
                </div>
              </div>

              <div className="overflow-auto max-h-[260px] custom-scrollbar">
                <table className="w-full">
                  <thead className="sticky top-0 bg-gradient-to-r from-gray-50 to-gray-100 z-10">
                    <tr>
                      <th className="text-left py-3 px-3 text-xs font-bold text-gray-700 uppercase tracking-wider border-b-2 border-gray-300">Client Name</th>
                      <th className="text-center py-3 px-3 text-xs font-bold text-gray-700 uppercase tracking-wider border-b-2 border-gray-300">Projects Ending</th>
                      <th className="text-center py-3 px-3 text-xs font-bold text-gray-700 uppercase tracking-wider border-b-2 border-gray-300">Extended</th>
                      <th className="text-center py-3 px-3 text-xs font-bold text-gray-700 uppercase tracking-wider border-b-2 border-gray-300">Not Extended</th>
                      <th className="text-center py-3 px-3 text-xs font-bold text-gray-700 uppercase tracking-wider border-b-2 border-gray-300">Extension %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingClientsData ? (
                      <tr>
                        <td colSpan="5" className="py-8 text-center">
                          <div className="flex items-center justify-center">
                            <div className="w-8 h-8 border-4 border-[#0047bb] border-t-transparent rounded-full animate-spin mr-3"></div>
                            <span className="text-gray-600">Loading client data...</span>
                          </div>
                        </td>
                      </tr>
                    ) : clientsData.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="py-8 text-center text-gray-600">No client data available</td>
                      </tr>
                    ) : (
                      clientsData.map((client, idx) => (
                        <tr key={idx} className="border-b border-gray-200 hover:bg-blue-50/50 transition-colors">
                          <td className="py-3 px-3 text-sm font-medium text-gray-900">{client.client}</td>
                          <td className="py-3 px-3 text-center text-sm font-medium text-gray-900">{client.projectsEnding}</td>
                          <td className="py-3 px-3 text-center">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-green-100 text-green-700 font-medium text-sm">{client.extended}</span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-100 text-red-700 font-medium text-sm">{client.notExtended}</span>
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center justify-center space-x-2">
                              <div className="flex-1 max-w-[120px]">
                                <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all duration-500 flex items-center justify-center text-[10px] font-medium text-white ${
                                      client.extensionRate >= 75 ? 'bg-green-500' :
                                      client.extensionRate >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                    }`}
                                    style={{ width: `${client.extensionRate}%` }}
                                  >
                                    {client.extensionRate >= 30 && `${client.extensionRate.toFixed(0)}%`}
                                  </div>
                                </div>
                              </div>
                              {client.extensionRate < 30 && (
                                <span className="text-xs font-medium text-gray-700">{client.extensionRate.toFixed(0)}%</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-xs text-gray-600 mb-1">Total Clients</div>
                    <div className="text-lg font-bold text-[#0047bb]">{isLoadingClientsData ? '...' : clientsData.length}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-600 mb-1">Total Ending</div>
                    <div className="text-lg font-bold text-gray-900">{isLoadingClientsData ? '...' : clientsData.reduce((sum, c) => sum + c.projectsEnding, 0)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-600 mb-1">Extended</div>
                    <div className="text-lg font-bold text-green-600">{isLoadingClientsData ? '...' : clientsData.reduce((sum, c) => sum + c.extended, 0)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-600 mb-1">Not Extended</div>
                    <div className="text-lg font-bold text-red-600">{isLoadingClientsData ? '...' : clientsData.reduce((sum, c) => sum + c.notExtended, 0)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Extension Frequency Heat Map */}
            <div className="bg-white/90 backdrop-blur-sm rounded-xl lg:rounded-2xl p-4 sm:p-5 border border-gray-200/50 shadow-xl shadow-[#0047bb]/5">
              <div className="flex items-center justify-between mb-4 sm:mb-5">
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-[#0047bb] mb-1">Extension Frequency Heat Map</h3>
                  <p className="text-xs sm:text-sm text-gray-600">Candidate concentration by extension count</p>
                </div>
                <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-[#0047bb]/10 to-[#0066ff]/10 rounded-xl flex items-center justify-center shadow-sm">
                  <Users className="w-5 h-5 text-[#0047bb]" />
                </div>
              </div>

              <div className="overflow-x-auto overflow-y-auto max-h-[240px] custom-scrollbar">
                <div className="w-full min-w-[500px]">
                  <div className="grid grid-cols-[minmax(100px,1fr)_repeat(4,minmax(80px,1fr))] gap-2 mb-2 sticky top-0 bg-white z-10 pb-2 border-b-2 border-gray-200">
                    <div className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center">Client Name</div>
                    {['1x Extended', '2x Extended', '3x Extended', '4x+ Extended'].map((cat, idx) => (
                      <div key={idx} className="text-xs font-bold text-center text-gray-700 uppercase tracking-wide flex items-center justify-center">{cat}</div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    {isLoadingFrequencyData ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                          <div className="w-8 h-8 border-4 border-[#0047bb] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                          <p className="text-gray-600 text-sm">Loading heat map data...</p>
                        </div>
                      </div>
                    ) : extensionFrequencyData.length === 0 ? (
                      <div className="text-center py-12">
                        <p className="text-gray-600 text-sm">No extension frequency data available</p>
                      </div>
                    ) : (
                      extensionFrequencyData.map((client, idx) => (
                        <div key={idx} className="grid grid-cols-[minmax(100px,1fr)_repeat(4,minmax(80px,1fr))] gap-2 items-center hover:bg-blue-50/50 p-2 rounded-lg transition-all duration-200">
                          <div className="text-sm font-medium text-gray-900 flex items-center">
                            <div className="w-2 h-2 rounded-full bg-[#0047bb] mr-2"></div>
                            {client.client}
                          </div>
                          {client.dist.map((value, catIdx) => {
                            const intensity = value / client.total;
                            const percentage = ((value / client.total) * 100).toFixed(0);
                            return (
                              <div key={catIdx} className="relative group">
                                <div
                                  className="h-11 rounded-lg flex flex-col items-center justify-center transition-all hover:scale-105 cursor-pointer shadow-md hover:shadow-lg border-2 border-white"
                                  style={{ backgroundColor: `rgba(0, 71, 187, ${0.15 + intensity * 0.75})` }}
                                >
                                  <div className={`text-sm font-medium ${intensity > 0.4 ? 'text-white' : 'text-[#0047bb]'}`}>{value}</div>
                                </div>
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-[10px] rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-20">
                                  {value} candidates ({percentage}%)
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-semibold text-gray-700">Intensity Scale:</span>
                    <span className="text-xs text-gray-600">Low Concentration</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    {[0.15, 0.35, 0.55, 0.75, 0.90].map((opacity, idx) => (
                      <div key={idx} className="w-8 h-3 rounded shadow-sm border border-gray-200" style={{ backgroundColor: `rgba(0, 71, 187, ${opacity})` }}></div>
                    ))}
                  </div>
                  <span className="text-xs text-gray-600">High Concentration</span>
                </div>
              </div>
            </div>

            {/* Extension Duration Trends */}
            <div className="bg-white/90 backdrop-blur-sm rounded-xl lg:rounded-2xl p-4 sm:p-5 border border-gray-200/50 shadow-xl shadow-[#0047bb]/5">
              <div className="flex items-center justify-between mb-4 sm:mb-5">
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-[#0047bb] mb-1">Extension Duration Trends</h3>
                  <p className="text-xs sm:text-sm text-gray-600">Average extension duration across clients</p>
                </div>
                <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-[#0047bb]/10 to-[#0066ff]/10 rounded-xl flex items-center justify-center shadow-sm">
                  <Activity className="w-5 h-5 text-[#0047bb]" />
                </div>
              </div>

              {isLoadingDurationData ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#0047bb] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                    <p className="text-gray-600 text-sm font-medium">Loading duration data...</p>
                  </div>
                </div>
              ) : extensionDurationData.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <BarChart3 className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600 text-sm">No extension duration data available</p>
                </div>
              ) : (
                <div className="relative" style={{ height: '325px' }}>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0047bb]/5 via-transparent to-transparent rounded-lg"></div>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={extensionDurationData} margin={{ top: 5, right: 20, left: 0, bottom: 10 }}>
                      <defs>
                        <linearGradient id="durationGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%"   stopColor="#0047bb" stopOpacity={0.9} />
                          <stop offset="25%"  stopColor="#0066ff" stopOpacity={0.7} />
                          <stop offset="50%"  stopColor="#0088ff" stopOpacity={0.5} />
                          <stop offset="75%"  stopColor="#33aaff" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#66ccff" stopOpacity={0.05} />
                        </linearGradient>
                        <filter id="glow">
                          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                          <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
                        </filter>
                        <filter id="softShadow">
                          <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#0047bb" floodOpacity="0.4" />
                        </filter>
                      </defs>
                      <CartesianGrid strokeDasharray="5 5" stroke="#e5e7eb" strokeOpacity={0.3} vertical={false} />
                      <XAxis dataKey="client" angle={-35} textAnchor="end" height={70} interval={0} tick={{ fill: '#6b7280', fontSize: 10, fontWeight: 600 }} axisLine={{ stroke: '#d1d5db', strokeWidth: 2 }} tickLine={{ stroke: '#d1d5db' }} />
                      <YAxis tick={{ fill: '#6b7280', fontSize: 11, fontWeight: 600 }} axisLine={{ stroke: '#d1d5db', strokeWidth: 2 }} tickLine={{ stroke: '#d1d5db' }} domain={[0, 12]} ticks={[0,1,2,3,4,5,6,7,8,9,10,11,12]} label={{ value: 'Months', angle: -90, position: 'insideLeft', style: { fill: '#0047bb', fontSize: 12, fontWeight: 700 } }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: 'rgba(255,255,255,0.98)', border: 'none', borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,71,187,0.25)', padding: '12px 16px' }}
                        labelStyle={{ color: '#0047bb', fontWeight: 700, fontSize: '13px', marginBottom: '8px', borderBottom: '2px solid #0047bb', paddingBottom: '6px' }}
                        itemStyle={{ color: '#1f2937', fontSize: '12px', fontWeight: 500, padding: '4px 0' }}
                        formatter={(value, name) => name === 'avgDuration' ? [`${value} months`, 'Average Duration'] : value}
                        cursor={{ stroke: '#0047bb', strokeWidth: 2, strokeDasharray: '5 5', strokeOpacity: 0.5 }}
                      />
                      <Area type="monotone" dataKey="avgDuration" stroke="#0047bb" strokeWidth={3} fill="url(#durationGradient)" fillOpacity={1} filter="url(#softShadow)" dot={false} activeDot={{ r: 6, fill: '#0047bb', stroke: '#fff', strokeWidth: 3, filter: 'url(#glow)' }} animationDuration={1500} animationEasing="ease-in-out" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT */}
          <div className="xl:col-span-4 space-y-4 sm:space-y-5 lg:space-y-6">

            {/* Top Clients by Extension Rate */}
            <div className="bg-white/90 backdrop-blur-sm rounded-xl lg:rounded-2xl p-4 sm:p-5 lg:p-6 border border-gray-200/50 shadow-xl shadow-[#0047bb]/5">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-[#0047bb] mb-1">Top Clients by Extension</h3>
                  <p className="text-xs text-gray-600">Ranked by extension rate</p>
                </div>
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-[#0047bb]/10 to-[#0066ff]/10 rounded-lg sm:rounded-xl flex items-center justify-center">
                  <Award className="w-4 h-4 sm:w-5 sm:h-5 text-[#0047bb]" />
                </div>
              </div>

              <div className="overflow-y-auto max-h-[230px] custom-scrollbar space-y-3 pr-2">
                {isLoadingClientsData ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-4 border-[#0047bb] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : clientsData.length === 0 ? (
                  <div className="text-center py-12 text-gray-600">No client data available</div>
                ) : (
                  clientsData
                    .sort((a, b) => b.extensionRate - a.extensionRate)
                    .map((client, idx) => (
                      <div key={idx} className="group">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                              idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-500 text-white shadow-lg shadow-yellow-500/30' :
                              idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white shadow-lg shadow-gray-400/30' :
                              idx === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-500 text-white shadow-lg shadow-orange-500/30' :
                              'bg-gradient-to-br from-blue-100 to-blue-200 text-[#0047bb]'
                            }`}>
                              {idx + 1}
                            </div>
                            <div>
                              <div className="text-sm font-bold text-gray-900">{client.client}</div>
                              <div className="text-xs text-gray-600">{client.extended}/{client.projectsEnding} extended</div>
                            </div>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-xs font-bold flex-shrink-0 ${
                            client.extensionRate >= 85 ? 'bg-green-100 text-green-700' :
                            client.extensionRate >= 70 ? 'bg-blue-100 text-blue-700' :
                            client.extensionRate >= 60 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {client.extensionRate.toFixed(1)}%
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              client.extensionRate >= 85 ? 'bg-gradient-to-r from-green-500 to-green-400' :
                              client.extensionRate >= 70 ? 'bg-gradient-to-r from-blue-500 to-blue-400' :
                              client.extensionRate >= 60 ? 'bg-gradient-to-r from-yellow-500 to-yellow-400' :
                              'bg-gradient-to-r from-red-500 to-red-400'
                            }`}
                            style={{ width: `${client.extensionRate}%` }}
                          ></div>
                        </div>
                      </div>
                    ))
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-xs text-gray-600 mb-1">Highest Rate</div>
                    <div className="text-lg font-bold text-green-600">
                      {isLoadingClientsData || clientsData.length === 0 ? '0.0' : Math.max(...clientsData.map(c => c.extensionRate)).toFixed(1)}%
                    </div>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-xs text-gray-600 mb-1">Average Rate</div>
                    <div className="text-lg font-bold text-[#0047bb]">
                      {isLoadingClientsData || clientsData.length === 0 ? '0.0' : (clientsData.reduce((sum, c) => sum + c.extensionRate, 0) / clientsData.length).toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Not Extended Analysis */}
            <div className="bg-white/90 backdrop-blur-sm rounded-xl lg:rounded-2xl p-4 sm:p-5 lg:p-6 border border-gray-200/50 shadow-xl shadow-[#0047bb]/5">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-[#0047bb]">Not Extended Analysis</h3>
                  <p className="text-xs text-gray-600">Reasons breakdown</p>
                </div>
              </div>

              {isLoadingReasonsData ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#0047bb] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading reasons data...</p>
                  </div>
                </div>
              ) : notExtendedReasons.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">No not-extended reasons available</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                  {notExtendedReasons.map((reason, idx) => {
                    const colors = ['#ef4444', '#f97316', '#f59e0b', '#8b5cf6', '#3b82f6'];
                    const bgColors = ['#fee2e2', '#ffedd5', '#fef3c7', '#f3e8ff', '#dbeafe'];
                    const percentage = reason.percentage;
                    const isEven = idx % 2 === 0;
                    return (
                      <div key={idx} className={`flex flex-col items-center ${isEven ? 'pt-0' : 'pt-6 sm:pt-6'}`}>
                        <div className="relative w-20 h-20 sm:w-24 sm:h-24">
                          <svg className="w-full h-full transform -rotate-90">
                            <circle cx="50%" cy="50%" r="38" stroke={bgColors[idx]} strokeWidth="8" fill="none" />
                            <circle cx="50%" cy="50%" r="38" stroke={colors[idx]} strokeWidth="8" fill="none" strokeDasharray={`${(percentage / 100) * 239} 239`} strokeLinecap="round" className="transition-all duration-1000" />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <div className="text-lg sm:text-xl font-bold" style={{ color: colors[idx] }}>{reason.count}</div>
                            <div className="text-[9px] sm:text-[10px] text-gray-600 font-medium">{percentage}%</div>
                          </div>
                        </div>
                        <div className="mt-1.5 sm:mt-2 text-center px-1">
                          <div className="text-[9px] sm:text-[10px] font-normal text-gray-700 leading-tight tracking-wide">{reason.reason}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Action Required */}
            <div className="bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 rounded-xl lg:rounded-2xl p-4 sm:p-5 border-2 border-red-200/50 shadow-xl shadow-red-500/10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-red-500/30 animate-pulse">
                    <AlertCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-red-600">Critical items needing immediate attention</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Extension Duration Stats */}
            <div className="bg-white/90 backdrop-blur-sm rounded-xl lg:rounded-2xl p-4 sm:p-5 lg:p-6 border border-gray-200/50 shadow-xl shadow-[#0047bb]/5">
              {!isLoadingDurationData && extensionDurationData.length > 0 && (
                <div className="space-y-3">
                  <div className="group relative bg-gradient-to-br from-blue-50 via-blue-100/50 to-blue-50 rounded-xl p-4 border border-blue-200/50 hover:shadow-lg hover:scale-105 transition-all duration-300 overflow-hidden">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-[#0047bb]/5 rounded-full -mr-8 -mt-8"></div>
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Average Duration</div>
                        <div className="w-8 h-8 bg-[#0047bb] rounded-lg flex items-center justify-center shadow-md">
                          <BarChart3 className="w-4 h-4 text-white" />
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-[#0047bb]">
                        {(extensionDurationData.reduce((sum, c) => sum + c.avgDuration, 0) / extensionDurationData.length).toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-600 font-medium mt-1">months average</div>
                    </div>
                  </div>

                  <div className="group relative bg-gradient-to-br from-green-50 via-green-100/50 to-green-50 rounded-xl p-4 border border-green-200/50 hover:shadow-lg hover:scale-105 transition-all duration-300 overflow-hidden">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-green-500/5 rounded-full -mr-8 -mt-8"></div>
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Maximum Duration</div>
                        <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center shadow-md">
                          <TrendingUp className="w-4 h-4 text-white" />
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-green-600">
                        {Math.max(...extensionDurationData.map(c => c.maxDuration))}
                      </div>
                      <div className="text-xs text-gray-600 font-medium mt-1">months peak</div>
                    </div>
                  </div>

                  <div className="group relative bg-gradient-to-br from-amber-50 via-amber-100/50 to-amber-50 rounded-xl p-4 border border-amber-200/50 hover:shadow-lg hover:scale-105 transition-all duration-300 overflow-hidden">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-amber-500/5 rounded-full -mr-8 -mt-8"></div>
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Minimum Duration</div>
                        <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center shadow-md">
                          <Minus className="w-4 h-4 text-white" />
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-amber-600">
                        {Math.min(...extensionDurationData.map(c => c.minDuration))}
                      </div>
                      <div className="text-xs text-gray-600 font-medium mt-1">months base</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f3f4f6; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #0047bb; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #003399; }
      `}</style>
    </div>
  );
};

export default AdminDashboard;